import { useEffect, useRef, useState } from "react";

import { prefersReducedMotion } from "./env";

type CursorMode = "default" | "link" | "view";

/**
 * A soft trailing cursor. It reads the element under the pointer and shifts
 * mode — subtle craft, not a gimmick. Hidden on touch / reduced-motion.
 */
export function CustomCursor() {
  const ringRef = useRef<HTMLDivElement>(null);
  const [mode, setMode] = useState<CursorMode>("default");
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (
      prefersReducedMotion() ||
      window.matchMedia("(pointer: coarse)").matches
    ) {
      return;
    }

    const target = { x: window.innerWidth / 2, y: window.innerHeight / 2 };
    const pos = { ...target };
    let frame = 0;

    const render = () => {
      pos.x += (target.x - pos.x) * 0.18;
      pos.y += (target.y - pos.y) * 0.18;
      if (ringRef.current) {
        ringRef.current.style.transform = `translate3d(${pos.x}px, ${pos.y}px, 0) translate(-50%, -50%)`;
      }
      frame = requestAnimationFrame(render);
    };
    frame = requestAnimationFrame(render);

    const onMove = (event: PointerEvent) => {
      target.x = event.clientX;
      target.y = event.clientY;
      setVisible(true);

      const el = event.target as HTMLElement | null;
      const interactive = el?.closest("a, button, [data-cursor]");
      if (!interactive) {
        setMode("default");
      } else if (interactive.getAttribute("data-cursor") === "view") {
        setMode("view");
      } else {
        setMode("link");
      }
    };

    const onLeave = () => setVisible(false);

    window.addEventListener("pointermove", onMove, { passive: true });
    window.addEventListener("pointerout", onLeave);

    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerout", onLeave);
    };
  }, []);

  return (
    <div
      ref={ringRef}
      className={`cursor cursor--${mode} ${visible ? "is-visible" : ""}`}
      aria-hidden="true"
    >
      <span>View</span>
    </div>
  );
}
